/**
 * BusinessGlossaryPage.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Enterprise Business Glossary — app-scoped, cross-source.
 *
 * Two tabs:
 *   1. Term List    — searchable table, inline edit, bulk approve, import/export
 *   2. Crosswalk    — groups by term → shows all source field mappings
 *                     clicking a source row fires onNavigateToField() which
 *                     changes the active tab to 'dg' in MyComponent
 *
 * Props:
 *   appNm              — ADRIVA application name
 *   onNavigateToField  — called when user clicks a crosswalk source row
 *                        { entityId, srcSystem, entity, field }
 *                        MyComponent should: setTabKey('dg'), set dataSrcSystemId,
 *                        set selectedDMEntityId, and pass a fieldFilter to GovernanceUI
 *
 * Design: light enterprise, Inter font, consistent with GovernanceUI palette.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiPost, apiDelete, apiPut } from "../API";

// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface GlossarySourceMapping {
  entity_id:        string;
  app_nm:           string;
  src_system:       string;
  entity:           string;
  field:            string;
  match_score:      number;
  match_type:       "matched" | "generated" | "manual";
  approved:         boolean;
  approved_by?:     string | null;
  approved_at?:     string | null;
  mapped_at?:       string;
  reassigned_from?: string | null;
}

export interface GlossaryTerm {
  id:             string;
  term:           string;
  description:    string;
  domain:         string;
  synonyms:       string[];
  status:         "generated" | "draft" | "approved" | "deprecated";
  source:         "generated" | "excel" | "collibra" | "alation";
  alation_id:     string | null;
  collibra_id:    string | null;
  imported_at:    string | null;
  source_mappings:GlossarySourceMapping[];
  created_at:     string;
  updated_at:     string;
  approved_by:    string | null;
  approved_at:    string | null;
}

export interface CrosswalkTerm {
  id:                  string;
  term:                string;
  description:         string;
  domain:              string;
  status:              string;
  source:              string;
  alation_id:          string | null;
  collibra_id:         string | null;
  approved_by:         string | null;
  total_mappings:      number;
  total_sources:       number;
  mappings_by_source:  Record<string, GlossarySourceMapping[]>;
}

export interface NavigateToFieldPayload {
  entityId:  string;
  srcSystem: string;
  entity:    string;
  field:     string;
}

interface BusinessGlossaryPageProps {
  appNm:             string;
  onNavigateToField: (p: NavigateToFieldPayload) => void;
}

interface GlossaryStats {
  total: number; approved: number; draft: number;
  generated: number; deprecated: number;
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const T = {
  bg:         "#eef1f6",
  bgCard:     "#ffffff",
  bgSidebar:  "#f5f7fa",
  textPrimary:"#0f1923",
  textSecond: "#1e2d3d",
  textMuted:  "#445566",
  textLabel:  "#2c3e52",
  brand:      "#1e3a6e",
  brandLight: "#dde7f5",
  brandMid:   "#90aed0",
  border:     "#c8d4e0",
  green:      "#166534",
  greenBg:    "#f0fdf4",
  greenBorder:"#86efac",
  amber:      "#7c2d00",
  amberBg:    "#fff7ed",
  amberBorder:"#fde68a",
  red:        "#991b1b",
  redBg:      "#fef2f2",
  redBorder:  "#fca5a5",
  purple:     "#6b21a8",
  purpleBg:   "#faf5ff",
  purpleBorder:"#d8b4fe",
};

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────────

const inputSt: React.CSSProperties = {
  background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 7,
  padding: "8px 12px", color: T.textPrimary, fontSize: "0.83rem",
  outline: "none", fontFamily: "inherit",
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; border: string; label: string }> = {
    approved:  { bg: T.greenBg,  color: T.green,  border: T.greenBorder,  label: "✓ Approved" },
    draft:     { bg: T.amberBg,  color: T.amber,  border: T.amberBorder,  label: "⏳ Draft" },
    generated: { bg: T.purpleBg, color: T.purple, border: T.purpleBorder, label: "🤖 Generated" },
    deprecated:{ bg: "#f8fafc",  color: T.textMuted, border: T.border,    label: "✕ Deprecated" },
  };
  const c = map[status] ?? map.draft;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px",
      borderRadius: 4, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    }}>{c.label}</span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const map: Record<string, { color: string; bg: string }> = {
    generated: { color: T.purple, bg: T.purpleBg },
    excel:     { color: T.green,  bg: T.greenBg },
    collibra:  { color: T.brand,  bg: T.brandLight },
    alation:   { color: T.green,  bg: T.greenBg },
  };
  const c = map[source] ?? { color: T.textMuted, bg: T.bgSidebar };
  return (
    <span style={{
      fontSize: "0.62rem", fontWeight: 700, padding: "1px 6px",
      borderRadius: 3, background: c.bg, color: c.color,
      border: `1px solid ${c.color}30`, textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}>{source}</span>
  );
}

function MatchScoreBar({ score }: { score: number }) {
  const color = score >= 0.9 ? T.green : score >= 0.7 ? T.brand : T.amber;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
      <div style={{ flex: 1, height: 4, background: T.border, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score * 100}%`, background: color, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: "0.68rem", fontWeight: 700, color, minWidth: 28 }}>
        {(score * 100).toFixed(0)}%
      </span>
    </div>
  );
}


// ─── REASSIGN MODAL ───────────────────────────────────────────────────────────
// Lets SME pick a different glossary term to map a field to.

// ─────────────────────────────────────────────────────────────────────────────
// FIELD PICKER MODAL
// ─────────────────────────────────────────────────────────────────────────────
// Completely redesigned: shows every field in the entity, grouped by source
// system, with each field's current term assignment.
//
// Three use cases handled in one modal:
//   A) REASSIGN:   user clicked 🔀 on an existing mapping → they pick a
//                  different field to take this term (or confirm this field
//                  stays but with a different term via the term-picker sub-row)
//   B) ADD:        user clicked + on a term with no mappings → they pick a
//                  field to assign this term to
//   C) SWAP:       the selected field already has a term → a warning shows
//                  and the existing term gets bumped off that field
//
// Props:
//   mode="reassign" → fromTermId/fromTermName/currentField are set
//   mode="add"      → just termId/termName, no current field
// ─────────────────────────────────────────────────────────────────────────────

interface FieldEntry {
  field:       string;
  entity:      string;
  src_system:  string;
  match_score: number;
  match_type:  string;
  approved:    boolean;
  term_id:     string | null;
  term_name:   string | null;
  term_status: string | null;
}

function FieldPickerModal({
  appNm,
  mode,
  termId,
  termName,
  entityId,
  currentField,
  onConfirm,
  onClose,
}: {
  appNm:         string;
  mode:          "reassign" | "add";
  termId:        string;
  termName:      string;
  entityId:      string;
  currentField?: string;           // present when mode="reassign"
  onConfirm:     (selectedField: FieldEntry) => void;
  onClose:       () => void;
}) {
  const [fields,   setFields]   = React.useState<FieldEntry[]>([]);
  const [loading,  setLoading]  = React.useState(true);
  const [search,   setSearch]   = React.useState("");
  const [selected, setSelected] = React.useState<FieldEntry | null>(null);
  const [saving,   setSaving]   = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiGet(`/adriva_api/dg/glossary/${appNm}/entity_fields/${entityId}`)
      .then((r: any) => {
        if (r.success) setFields(r.fields);
      })
      .finally(() => setLoading(false));
  }, [appNm, entityId]);

  // Group fields by src_system
  const grouped = React.useMemo(() => {
    const filtered = fields.filter(f => {
      if (f.field === currentField) return false; // exclude the field we're moving FROM
      const q = search.toLowerCase();
      return !q || f.field.toLowerCase().includes(q) || f.entity.toLowerCase().includes(q);
    });
    const m = new Map<string, FieldEntry[]>();
    filtered.forEach(f => {
      const arr = m.get(f.src_system) ?? [];
      arr.push(f);
      m.set(f.src_system, arr);
    });
    return m;
  }, [fields, search, currentField]);

  // Fields with no term at all (unassigned) — shown in an "Unassigned" group
  const unassignedCount = React.useMemo(
    () => [...grouped.values()].flat().filter(f => !f.term_id).length,
    [grouped]
  );

  const alreadyHasTerm = selected && selected.term_id && selected.term_id !== termId;

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      onConfirm(selected);
    } finally {
      setSaving(false);
    }
  };

  const headerColor = mode === "reassign" ? T.amber : T.brand;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(15,25,35,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: T.bgCard, borderRadius: 12,
        width: "min(720px, 96vw)", maxHeight: "86vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)",
      }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          padding: "16px 20px 14px",
          borderBottom: `1px solid ${T.border}`,
          borderTop: `3px solid ${headerColor}`,
        }}>
          <div style={{ fontSize: "0.6rem", fontWeight: 700, color: headerColor,
            textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 5 }}>
            {mode === "reassign" ? "🔀 Reassign Field Mapping" : "＋ Add Field Mapping"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: "0.9rem", fontWeight: 700, color: T.textPrimary }}>
              Business Term:
            </div>
            <span style={{
              fontSize: "0.88rem", fontWeight: 700, color: headerColor,
              background: mode === "reassign" ? T.amberBg : T.brandLight,
              border: `1px solid ${mode === "reassign" ? T.amberBorder : T.brandMid}`,
              borderRadius: 5, padding: "2px 10px",
            }}>{termName}</span>
          </div>
          {mode === "reassign" && currentField && (
            <div style={{ fontSize: "0.74rem", color: T.textMuted, marginTop: 5 }}>
              Moving mapping from field{" "}
              <span style={{ fontFamily: "monospace", fontWeight: 700,
                color: T.textSecond }}>{currentField}</span>
              . Select the field that should represent this concept instead.
            </div>
          )}
          {mode === "add" && (
            <div style={{ fontSize: "0.74rem", color: T.textMuted, marginTop: 5 }}>
              Select a field to link to this business term.
              {unassignedCount > 0 && (
                <span style={{ color: T.brand }}> {unassignedCount} unassigned field{unassignedCount !== 1 ? "s" : ""} shown first.</span>
              )}
            </div>
          )}
        </div>

        {/* ── Search ─────────────────────────────────────────────────────── */}
        <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.border}`,
          background: T.bgSidebar }}>
          <input
            autoFocus
            placeholder="🔍 Filter fields by name or entity…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "8px 12px",
              border: `1px solid ${T.border}`, borderRadius: 7,
              fontSize: "0.83rem", outline: "none", fontFamily: "inherit",
              background: T.bgCard, color: T.textPrimary,
            }}
          />
        </div>

        {/* ── Field list ─────────────────────────────────────────────────── */}
        <div style={{ overflowY: "auto", flexGrow: 1 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted,
              fontSize: "0.82rem" }}>
              Loading fields…
            </div>
          ) : grouped.size === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted,
              fontSize: "0.82rem", fontStyle: "italic" }}>
              {search ? "No fields match the search." : "No fields found for this entity."}
            </div>
          ) : (
            [...grouped.entries()].map(([srcSys, srcFields]) => (
              <div key={srcSys}>
                {/* Source system header */}
                <div style={{
                  padding: "8px 20px", background: T.bgSidebar,
                  borderBottom: `1px solid ${T.border}`,
                  borderTop: `1px solid ${T.border}`,
                  fontSize: "0.68rem", fontWeight: 800, color: T.brand,
                  display: "flex", alignItems: "center", gap: 8,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                }}>
                  <span style={{
                    background: T.brandLight, border: `1px solid ${T.brandMid}`,
                    borderRadius: 4, padding: "1px 8px",
                  }}>🗄 {srcSys}</span>
                  <span style={{ fontWeight: 400, color: T.textMuted, textTransform: "none" }}>
                    {srcFields.length} field{srcFields.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Field rows */}
                {srcFields.map((f, i) => {
                  const isSelected = selected?.field === f.field &&
                                     selected?.src_system === f.src_system;
                  const isUnassigned = !f.term_id;
                  const hasDifferentTerm = f.term_id && f.term_id !== termId;
                  const hasSameTerm = f.term_id === termId;

                  return (
                    <div
                      key={`${f.src_system}|${f.field}`}
                      onClick={() => setSelected(isSelected ? null : f)}
                      style={{
                        padding: "10px 20px", cursor: "pointer",
                        borderBottom: i < srcFields.length - 1
                          ? `1px solid ${T.border}` : "none",
                        background: isSelected ? T.brandLight
                          : isUnassigned ? "#fffef5"
                          : T.bgCard,
                        borderLeft: isSelected
                          ? `3px solid ${T.brand}`
                          : hasSameTerm
                            ? `3px solid ${T.green}`
                            : "3px solid transparent",
                        display: "flex", alignItems: "center", gap: 12,
                        transition: "all 0.1s",
                      }}
                    >
                      {/* Selection indicator */}
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${isSelected ? T.brand : T.border}`,
                        background: isSelected ? T.brand : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {isSelected && (
                          <span style={{ color: "#fff", fontSize: "0.6rem",
                            fontWeight: 800, lineHeight: 1 }}>✓</span>
                        )}
                      </div>

                      {/* Field info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            fontSize: "0.84rem", fontWeight: isSelected ? 700 : 600,
                            color: isSelected ? T.brand : T.textPrimary,
                            fontFamily: "monospace",
                          }}>{f.field}</span>
                          <span style={{ fontSize: "0.7rem", color: T.textMuted }}>{f.entity}</span>
                        </div>

                        {/* Current term assignment */}
                        <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                          {isUnassigned ? (
                            <span style={{
                              fontSize: "0.65rem", color: T.amber, fontWeight: 700,
                              background: T.amberBg, border: `1px solid ${T.amberBorder}`,
                              borderRadius: 3, padding: "1px 6px",
                            }}>⊘ No term assigned</span>
                          ) : hasSameTerm ? (
                            <span style={{
                              fontSize: "0.65rem", color: T.green, fontWeight: 700,
                              background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                              borderRadius: 3, padding: "1px 6px",
                            }}>✓ Already mapped to "{f.term_name}"</span>
                          ) : (
                            <>
                              <span style={{ fontSize: "0.65rem", color: T.textMuted }}>
                                Currently:
                              </span>
                              <span style={{
                                fontSize: "0.68rem", fontWeight: 700, color: T.textSecond,
                                background: T.bgSidebar, border: `1px solid ${T.border}`,
                                borderRadius: 3, padding: "1px 7px",
                              }}>{f.term_name}</span>
                              {hasDifferentTerm && isSelected && (
                                <span style={{
                                  fontSize: "0.62rem", color: T.amber,
                                  fontWeight: 600,
                                }}>⚠ Will replace this mapping</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Match score */}
                      {!isUnassigned && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4,
                          flexShrink: 0 }}>
                          <div style={{ width: 40, height: 3, background: T.border,
                            borderRadius: 99, overflow: "hidden" }}>
                            <div style={{
                              height: "100%",
                              width: `${(f.match_score ?? 0) * 100}%`,
                              background: f.match_score >= 0.9 ? T.green
                                : f.match_score >= 0.7 ? T.brand : T.amber,
                              borderRadius: 99,
                            }}/>
                          </div>
                          <span style={{
                            fontSize: "0.62rem", fontWeight: 700,
                            color: f.match_score >= 0.9 ? T.green
                              : f.match_score >= 0.7 ? T.brand : T.amber,
                            minWidth: 26,
                          }}>{((f.match_score ?? 0) * 100).toFixed(0)}%</span>
                        </div>
                      )}

                      {/* Approval badge */}
                      {!isUnassigned && (
                        <span style={{
                          fontSize: "0.6rem", flexShrink: 0, padding: "1px 6px",
                          borderRadius: 3, fontWeight: 700,
                          background: f.approved ? T.greenBg : "#f8fafc",
                          color: f.approved ? T.green : T.textMuted,
                          border: `1px solid ${f.approved ? T.greenBorder : T.border}`,
                        }}>{f.approved ? "✓" : "⏳"}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* ── Warning when selected field already has a different term ────── */}
        {alreadyHasTerm && (
          <div style={{
            padding: "10px 20px", background: T.amberBg,
            borderTop: `1px solid ${T.amberBorder}`,
            fontSize: "0.76rem", color: T.amber, lineHeight: 1.55,
          }}>
            ⚠ <strong>{selected!.field}</strong> is currently mapped to{" "}
            <strong>"{selected!.term_name}"</strong>. Confirming will remove that
            mapping and assign <strong>"{termName}"</strong> here instead. The
            displaced term will keep its other mappings.
          </div>
        )}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: T.bgSidebar,
        }}>
          <button onClick={onClose} style={{
            padding: "7px 16px", borderRadius: 6, cursor: "pointer",
            background: T.bgCard, border: `1px solid ${T.border}`,
            color: T.textMuted, fontSize: "0.78rem",
          }}>Cancel</button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {selected && (
              <span style={{ fontSize: "0.72rem", color: T.textMuted }}>
                Selected: <strong style={{ color: T.textPrimary,
                  fontFamily: "monospace" }}>{selected.field}</strong>
              </span>
            )}
            <button
              onClick={handleConfirm}
              disabled={!selected || saving || selected.term_id === termId}
              style={{
                padding: "8px 22px", borderRadius: 6, border: "none",
                cursor: selected && selected.term_id !== termId ? "pointer" : "default",
                background: selected && selected.term_id !== termId
                  ? headerColor : T.border,
                color: "#fff", fontSize: "0.78rem", fontWeight: 700,
                opacity: selected && selected.term_id !== termId ? 1 : 0.5,
              }}
            >
              {saving ? "Saving…"
                : mode === "reassign" ? "🔀 Confirm Reassign"
                : "＋ Add Mapping"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}




// ─── MAPPING ROW ─────────────────────────────────────────────────────────────
// Reusable row for a single source_mapping — used in both TermRow (expanded)
// and CrosswalkTab. Handles Approve / Reject / Reassign in-place.

function MappingRow({
  mapping, termId, termName, appNm, onApproved, onReassigned, showNavigate, onNavigate,
}: {
  mapping:       GlossarySourceMapping;
  termId:        string;
  termName:      string;
  appNm:         string;
  onApproved:    (fieldName: string, approved: boolean) => void;
  onReassigned:  (fieldName: string, toTermId: string) => void;
  showNavigate?: boolean;
  onNavigate?:   () => void;
}) {
  const [busy,        setBusy]        = React.useState<"approve"|"reject"|null>(null);
  const [showPicker,  setShowPicker]  = React.useState(false);

  const handleApprove = async (approved: boolean) => {
    setBusy(approved ? "approve" : "reject");
    try {
      const r = await apiPost(`/adriva_api/dg/glossary/${appNm}/approve_mapping`, {
        term_id:     termId,
        entity_id:   mapping.entity_id,
        field_name:  mapping.field,
        approved,
        approved_by: "SME",
      });
      if (r.success) onApproved(mapping.field, approved);
    } finally {
      setBusy(null);
    }
  };

  // Called when SME picks a target field in the FieldPickerModal
  const handleFieldPicked = async (targetField: FieldEntry) => {
    setShowPicker(false);

    // If the target field already has a DIFFERENT term, we need to first
    // remove it from that term before assigning our term.  The backend
    // reassign_mapping endpoint handles this automatically when from_term_id
    // is provided.  When we're just moving this mapping's field, we use a
    // two-step: reassign current mapping → targetField on our term.
    //
    // Backend call: reassign_mapping
    //   from_term_id = termId (our term, current field = mapping.field)
    //   to_term_id   = termId (same term, we just change which field it covers)
    // — this is a "field swap on same term" which the backend handles by
    //   removing the old source_mapping and adding a new one for targetField.
    //
    // HOWEVER the current backend reassign endpoint moves mappings BETWEEN
    // terms, not fields within a term. So we use a simpler approach:
    //   1. DELETE approval on current field (approve_mapping approved=false)
    //   2. POST upsert with new source_mapping for targetField
    //   3. If targetField had a different term → reassign_mapping removes it

    try {
      // If targetField has a different current term, remove that assignment
      if (targetField.term_id && targetField.term_id !== termId) {
        await apiPost(`/adriva_api/dg/glossary/${appNm}/reassign_mapping`, {
          from_term_id: targetField.term_id,
          to_term_id:   termId,
          entity_id:    mapping.entity_id,
          field_name:   targetField.field,
          approved_by:  "SME",
        });
      } else if (!targetField.term_id) {
        // Unassigned field: upsert a new source_mapping on our term
        await apiPost(`/adriva_api/dg/glossary/${appNm}/upsert`, {
          terms: [{
            id: termId,
            source_mapping: {
              entity_id:   mapping.entity_id,
              app_nm:      appNm,
              src_system:  targetField.src_system,
              entity:      targetField.entity,
              field:       targetField.field,
              match_score: 1.0,
              match_type:  "manual",
              approved:    true,
              approved_by: "SME",
            },
          }],
        });
      }

      // Move our current mapping.field off our term (it moved to targetField)
      // by calling reassign_mapping from our term to a "no-term" state.
      // Since we can't assign null, we just approve_mapping=false to mark
      // it pending, then the parent refresh will fix up the display.
      await apiPost(`/adriva_api/dg/glossary/${appNm}/approve_mapping`, {
        term_id:    termId,
        entity_id:  mapping.entity_id,
        field_name: mapping.field,
        approved:   false,
      });

      onReassigned(mapping.field, termId);
    } catch (e) {
      console.error("Field pick failed", e);
    }
  };

  return (
    <>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 10px", borderRadius: 7,
        background: mapping.approved ? T.greenBg : "#fff",
        border: `1px solid ${mapping.approved ? T.greenBorder : T.border}`,
        transition: "background 0.15s",
      }}>
        {/* Source system badge */}
        <span style={{
          fontSize: "0.68rem", fontWeight: 800, color: T.brand,
          background: T.brandLight, borderRadius: 3, padding: "1px 7px",
          border: `1px solid ${T.brandMid}`, flexShrink: 0,
        }}>{mapping.src_system}</span>

        {/* Entity → Field */}
        <span style={{ fontSize: "0.74rem", fontWeight: 600, color: T.textSecond,
          flexShrink: 0 }}>{mapping.entity}</span>
        <span style={{ fontSize: "0.68rem", color: T.textMuted }}>→</span>
        <span style={{ fontSize: "0.78rem", fontFamily: "monospace", color: T.brand,
          flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap" }}>{mapping.field}</span>

        {/* Match score */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <div style={{ width: 44, height: 3, background: T.border, borderRadius: 99,
            overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${mapping.match_score * 100}%`,
              background: mapping.match_score >= 0.9 ? T.green
                : mapping.match_score >= 0.7 ? T.brand : T.amber,
              borderRadius: 99,
            }}/>
          </div>
          <span style={{
            fontSize: "0.65rem", fontWeight: 700, minWidth: 26,
            color: mapping.match_score >= 0.9 ? T.green
              : mapping.match_score >= 0.7 ? T.brand : T.amber,
          }}>{(mapping.match_score * 100).toFixed(0)}%</span>
        </div>

        {/* Match type chip */}
        <span style={{
          fontSize: "0.6rem", padding: "1px 5px", borderRadius: 3, flexShrink: 0,
          background: mapping.match_type === "matched" ? T.greenBg
            : mapping.match_type === "manual" ? T.amberBg : "#faf5ff",
          color: mapping.match_type === "matched" ? T.green
            : mapping.match_type === "manual" ? T.amber : "#6b21a8",
          border: `1px solid ${mapping.match_type === "matched" ? T.greenBorder
            : mapping.match_type === "manual" ? T.amberBorder : "#d8b4fe"}`,
          fontWeight: 700,
        }}>{mapping.match_type}</span>

        {/* Action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          {mapping.approved ? (
            <>
              <span style={{ fontSize: "0.68rem", color: T.green, fontWeight: 700,
                marginRight: 2 }}>
                ✓{mapping.approved_by ? ` ${mapping.approved_by}` : ""}
              </span>
              <button onClick={() => handleApprove(false)} disabled={busy !== null}
                style={{ padding: "2px 7px", borderRadius: 4, cursor: "pointer",
                  background: T.redBg, border: `1px solid ${T.redBorder}`,
                  color: T.red, fontSize: "0.63rem", fontWeight: 700 }}>
                {busy === "reject" ? "…" : "✕"}
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: "0.65rem", color: T.textMuted, marginRight: 2 }}>⏳</span>
              <button onClick={() => handleApprove(true)} disabled={busy !== null}
                style={{ padding: "2px 7px", borderRadius: 4, cursor: "pointer",
                  background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                  color: T.green, fontSize: "0.63rem", fontWeight: 700 }}>
                {busy === "approve" ? "…" : "✓"}
              </button>
              <button onClick={() => handleApprove(false)} disabled={busy !== null}
                style={{ padding: "2px 7px", borderRadius: 4, cursor: "pointer",
                  background: T.redBg, border: `1px solid ${T.redBorder}`,
                  color: T.red, fontSize: "0.63rem", fontWeight: 700 }}>
                {busy === "reject" ? "…" : "✕"}
              </button>
            </>
          )}
          <button
            onClick={() => setShowPicker(true)} disabled={busy !== null}
            title="Move this field to a different term, or pick a different field for this term"
            style={{ padding: "2px 7px", borderRadius: 4, cursor: "pointer",
              background: T.amberBg, border: `1px solid ${T.amberBorder}`,
              color: T.amber, fontSize: "0.63rem", fontWeight: 700 }}>
            🔀
          </button>
          {showNavigate && onNavigate && (
            <button onClick={e => { e.stopPropagation(); onNavigate(); }}
              style={{ padding: "2px 7px", borderRadius: 4, cursor: "pointer",
                background: T.brandLight, border: `1px solid ${T.brandMid}`,
                color: T.brand, fontSize: "0.63rem", fontWeight: 600 }}>→ DG
            </button>
          )}
        </div>
      </div>

      {showPicker && (
        <FieldPickerModal
          appNm={appNm}
          mode="reassign"
          termId={termId}
          termName={termName}
          entityId={mapping.entity_id}
          currentField={mapping.field}
          onConfirm={handleFieldPicked}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}



// ─── INLINE EDIT TERM ROW ─────────────────────────────────────────────────────

function TermRow({
  term, expanded, onToggle, onApprove, onDeprecate, onSave,
  appNm, onMappingChange,
}: {
  term: GlossaryTerm;
  expanded: boolean;
  onToggle: () => void;
  onApprove: (termId: string, unapprove?: boolean) => void;
  onDeprecate: (termId: string) => void;
  onSave: (termId: string, changes: Partial<GlossaryTerm>) => void;
  appNm: string;
  onMappingChange: () => void;  // refresh parent after approve/reassign
}) {
  const [editing,   setEditing]   = useState(false);
  const [draftTerm, setDraftTerm] = useState(term.term);
  const [draftDesc, setDraftDesc] = useState(term.description);
  const [draftDom,  setDraftDom]  = useState(term.domain);
  // Local optimistic state for source_mappings
  const [liveMappings, setLiveMappings] = useState<GlossarySourceMapping[]>(term.source_mappings);
  const [showAddPicker, setShowAddPicker] = useState(false);
  React.useEffect(() => { setLiveMappings(term.source_mappings); }, [term.source_mappings]);

  const handleSave = () => {
    onSave(term.id, { term: draftTerm, description: draftDesc, domain: draftDom });
    setEditing(false);
  };

  const handleMappingApproved = (fieldName: string, approved: boolean) => {
    setLiveMappings(prev => prev.map(m =>
      m.field === fieldName ? { ...m, approved, approved_by: approved ? "SME" : null } : m
    ));
    onMappingChange();
  };

  const handleMappingReassigned = (fieldName: string, _toTermId: string) => {
    // Remove the mapping from this term's live view (it moved to another term)
    setLiveMappings(prev => prev.filter(m => m.field !== fieldName));
    onMappingChange();
  };

  const handleMappingAdded = (_targetField: FieldEntry) => {
    setShowAddPicker(false);
    onMappingChange(); // full refresh to pick up new source_mapping
  };

  const srcCount = new Set(term.source_mappings.map(m => m.src_system)).size;

  return (
    <>
      <tr style={{
        background: expanded ? T.brandLight : "transparent",
        borderBottom: `1px solid ${T.border}`,
        cursor: "pointer",
        transition: "background 0.1s",
      }}>
        {/* Expand toggle */}
        <td style={{ padding: "10px 12px", width: 32 }} onClick={onToggle}>
          <span style={{
            fontSize: "0.7rem", color: expanded ? T.brand : T.textMuted,
            transform: expanded ? "rotate(90deg)" : "none",
            display: "inline-block", transition: "transform 0.15s",
          }}>▶</span>
        </td>

        {/* Term */}
        <td style={{ padding: "10px 12px" }} onClick={() => !editing && onToggle()}>
          {editing ? (
            <input style={{ ...inputSt, width: "100%" }} value={draftTerm}
              onChange={e => setDraftTerm(e.target.value)} onClick={e => e.stopPropagation()} />
          ) : (
            <div>
              <span style={{ fontSize: "0.84rem", fontWeight: 700, color: T.textPrimary }}>{term.term}</span>
              {term.synonyms?.length > 0 && (
                <div style={{ fontSize: "0.68rem", color: T.textMuted, marginTop: 2 }}>
                  Also: {term.synonyms.slice(0, 3).join(", ")}
                </div>
              )}
            </div>
          )}
        </td>

        {/* Description */}
        <td style={{ padding: "10px 12px", maxWidth: 280 }}>
          {editing ? (
            <textarea style={{ ...inputSt, width: "100%", resize: "vertical", minHeight: 56 } as React.CSSProperties}
              value={draftDesc} onChange={e => setDraftDesc(e.target.value)}
              onClick={e => e.stopPropagation()} />
          ) : (
            <span style={{ fontSize: "0.78rem", color: T.textMuted, lineHeight: 1.4,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>
              {term.description || <span style={{ fontStyle: "italic" }}>No description</span>}
            </span>
          )}
        </td>

        {/* Domain */}
        <td style={{ padding: "10px 12px" }}>
          {editing ? (
            <input style={{ ...inputSt, width: 120 }} value={draftDom}
              onChange={e => setDraftDom(e.target.value)} onClick={e => e.stopPropagation()} />
          ) : (
            <span style={{ fontSize: "0.78rem", color: T.textMuted }}>{term.domain || "—"}</span>
          )}
        </td>

        {/* Status */}
        <td style={{ padding: "10px 12px" }}><StatusBadge status={term.status} /></td>

        {/* Source */}
        <td style={{ padding: "10px 12px" }}><SourceBadge source={term.source} /></td>

        {/* Mappings */}
        <td style={{ padding: "10px 12px" }}>
          {srcCount > 0 ? (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: "0.72rem", fontWeight: 700, color: T.brand,
              background: T.brandLight, border: `1px solid ${T.brandMid}`,
              borderRadius: 5, padding: "2px 8px",
            }}>
              🔗 {srcCount} source{srcCount !== 1 ? "s" : ""}
            </span>
          ) : (
            <span style={{ fontSize: "0.72rem", color: T.textMuted, fontStyle: "italic" }}>none</span>
          )}
        </td>

        {/* Actions */}
        <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            {editing ? (
              <>
                <button onClick={handleSave} style={{
                  padding: "3px 10px", borderRadius: 5, cursor: "pointer",
                  background: T.brand, border: `1px solid ${T.brand}`,
                  color: "#fff", fontSize: "0.7rem", fontWeight: 700,
                }}>Save</button>
                <button onClick={() => setEditing(false)} style={{
                  padding: "3px 10px", borderRadius: 5, cursor: "pointer",
                  background: T.bgCard, border: `1px solid ${T.border}`,
                  color: T.textMuted, fontSize: "0.7rem",
                }}>Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} style={{
                  padding: "3px 8px", borderRadius: 5, cursor: "pointer",
                  background: T.bgCard, border: `1px solid ${T.border}`,
                  color: T.textMuted, fontSize: "0.7rem",
                }}>✎</button>
                {term.status !== "approved" ? (
                  <button onClick={() => onApprove(term.id)} style={{
                    padding: "3px 8px", borderRadius: 5, cursor: "pointer",
                    background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                    color: T.green, fontSize: "0.7rem", fontWeight: 700,
                  }}>✓ Approve</button>
                ) : (
                  <button onClick={() => onApprove(term.id, true)} style={{
                    padding: "3px 8px", borderRadius: 5, cursor: "pointer",
                    background: T.bgCard, border: `1px solid ${T.border}`,
                    color: T.textMuted, fontSize: "0.7rem",
                  }}>Unapprove</button>
                )}
                <button onClick={() => onDeprecate(term.id)} style={{
                  padding: "3px 8px", borderRadius: 5, cursor: "pointer",
                  background: T.redBg, border: `1px solid ${T.redBorder}`,
                  color: T.red, fontSize: "0.7rem",
                }}>✕</button>
              </>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded crosswalk inline — with approve/reject/reassign actions */}
      {expanded && liveMappings.length > 0 && (
        <tr style={{ background: T.bgSidebar }}>
          <td colSpan={8} style={{ padding: "0 12px 14px 44px" }}>
            <div style={{
              fontSize: "0.68rem", fontWeight: 700, color: T.textMuted,
              textTransform: "uppercase", letterSpacing: "0.08em",
              marginBottom: 8, marginTop: 10,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              Source Mappings
              <span style={{ fontWeight: 400, color: T.textMuted, textTransform: "none" }}>
                — approve each field→term link or reassign to the correct concept
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {liveMappings.map((m, i) => (
                <MappingRow
                  key={`${m.entity_id}|${m.field}`}
                  mapping={m}
                  termId={term.id}
                  termName={term.term}
                  appNm={appNm}
                  onApproved={handleMappingApproved}
                  onReassigned={handleMappingReassigned}
                />
              ))}
            </div>
            {liveMappings.length === 0 && (
              <div style={{ fontSize: "0.76rem", color: T.textMuted, fontStyle: "italic" }}>
                All mappings have been reassigned to other terms.
              </div>
            )}

            {/* ＋ Add field mapping button */}
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setShowAddPicker(true)}
                style={{
                  padding: "5px 14px", borderRadius: 6, cursor: "pointer",
                  background: T.brandLight, border: `1px dashed ${T.brandMid}`,
                  color: T.brand, fontSize: "0.72rem", fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 6,
                  transition: "all 0.15s",
                }}>
                ＋ Add field mapping
              </button>
            </div>

            {/* FieldPickerModal for "add" mode (no current field to move from) */}
            {showAddPicker && (
              <FieldPickerModal
                appNm={appNm}
                mode="add"
                termId={term.id}
                termName={term.term}
                entityId={liveMappings[0]?.entity_id ?? ""}
                onConfirm={handleMappingAdded}
                onClose={() => setShowAddPicker(false)}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── CROSSWALK TAB ────────────────────────────────────────────────────────────

function CrosswalkTab({
  appNm, filters, onNavigateToField, onMappingChange,
}: {
  appNm: string;
  filters: { domain: string; entity: string; status: string; search: string };
  onNavigateToField: (p: NavigateToFieldPayload) => void;
  onMappingChange: () => void;
}) {
  const [terms,   setTerms]   = useState<CrosswalkTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded,setExpanded]= useState<Set<string>>(new Set());
  // Local optimistic mapping state: termId → { fieldKey → GlossarySourceMapping }
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, Record<string, GlossarySourceMapping>>
  >({});

  const applyMappingApproved = (termId: string, fieldName: string, approved: boolean) => {
    setLocalOverrides(prev => {
      const termOverrides = { ...(prev[termId] ?? {}) };
      // find the mapping in terms
      const termEntry = terms.find(t => t.id === termId);
      if (!termEntry) return prev;
      const allMaps = Object.values(termEntry.mappings_by_source).flat();
      const m = allMaps.find(m => m.field === fieldName);
      if (!m) return prev;
      termOverrides[fieldName] = { ...m, approved, approved_by: approved ? "SME" : null };
      return { ...prev, [termId]: termOverrides };
    });
    onMappingChange();
  };

  const applyMappingReassigned = (termId: string, fieldName: string) => {
    // Remove the field from this term's display
    setLocalOverrides(prev => {
      const termOverrides = { ...(prev[termId] ?? {}), [fieldName]: null as any };
      return { ...prev, [termId]: termOverrides };
    });
    onMappingChange();
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.domain) params.set("domain", filters.domain);
    if (filters.entity) params.set("entity", filters.entity);
    if (filters.status) params.set("status", filters.status);
    if (filters.search) params.set("search", filters.search);
    const r = await apiGet(`/adriva_api/dg/glossary/${appNm}/crosswalk?${params}`);
    if (r.success) setTerms(r.terms);
    setLoading(false);
  }, [appNm, filters.domain, filters.entity, filters.status, filters.search]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
      padding: 60, flexDirection: "column", gap: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%",
        border: `3px solid ${T.brandMid}`, borderTopColor: T.brand,
        animation: "spin 0.8s linear infinite" }} />
      <span style={{ fontSize: "0.84rem", color: T.textMuted }}>Loading crosswalk…</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!terms.length) return (
    <div style={{ padding: "48px 24px", textAlign: "center", color: T.textMuted }}>
      <div style={{ fontSize: "2rem", marginBottom: 10 }}>🔗</div>
      <div style={{ fontWeight: 700, color: T.textSecond, marginBottom: 6, fontSize: "0.9rem" }}>
        No crosswalk data yet
      </div>
      <div style={{ fontSize: "0.82rem" }}>
        Run a DG discovery to populate the crosswalk. Business terms will appear here after the first source is processed.
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "16px 0" }}>
      {terms.map(term => {
        const open = expanded.has(term.id);
        const sources = Object.keys(term.mappings_by_source);
        const allMappings = Object.values(term.mappings_by_source).flat();
        const approvedCount = allMappings.filter(m => m.approved).length;

        return (
          <div key={term.id} style={{
            border: `1px solid ${open ? T.brand : T.border}`,
            borderRadius: 10, overflow: "hidden", background: T.bgCard,
            boxShadow: open ? "0 2px 12px rgba(30,58,110,0.08)" : "none",
          }}>
            {/* Term header */}
            <div onClick={() => toggle(term.id)} style={{
              display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
              cursor: "pointer", background: open ? T.bgSidebar : T.bgCard,
              borderBottom: open ? `1px solid ${T.border}` : "none",
              userSelect: "none",
            }}>
              <span style={{
                fontSize: "0.7rem", color: open ? T.brand : T.textMuted,
                transform: open ? "rotate(90deg)" : "none",
                display: "inline-block", transition: "transform 0.15s",
              }}>▶</span>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: 700, color: T.textPrimary,
                    fontFamily: "'Lora',Georgia,serif" }}>{term.term}</span>
                  <StatusBadge status={term.status} />
                  <SourceBadge source={term.source} />
                  {term.alation_id && (
                    <span style={{ fontSize: "0.62rem", color: T.green, background: T.greenBg,
                      border: `1px solid ${T.greenBorder}`, borderRadius: 3, padding: "1px 5px" }}>
                      ◈ Alation
                    </span>
                  )}
                  {term.collibra_id && (
                    <span style={{ fontSize: "0.62rem", color: T.brand, background: T.brandLight,
                      border: `1px solid ${T.brandMid}`, borderRadius: 3, padding: "1px 5px" }}>
                      ⬡ Collibra
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "0.75rem", color: T.textMuted, marginTop: 3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {term.description || <span style={{ fontStyle: "italic" }}>No description</span>}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <span style={{
                  fontSize: "0.7rem", fontWeight: 700, color: T.brand,
                  background: T.brandLight, border: `1px solid ${T.brandMid}`,
                  borderRadius: 10, padding: "2px 10px",
                }}>
                  {sources.length} source{sources.length !== 1 ? "s" : ""}
                </span>
                <span style={{
                  fontSize: "0.7rem", fontWeight: 700,
                  color: approvedCount === allMappings.length && allMappings.length > 0 ? T.green : T.amber,
                  background: approvedCount === allMappings.length && allMappings.length > 0 ? T.greenBg : T.amberBg,
                  border: `1px solid ${approvedCount === allMappings.length && allMappings.length > 0 ? T.greenBorder : T.amberBorder}`,
                  borderRadius: 10, padding: "2px 10px",
                }}>
                  {approvedCount}/{allMappings.length} approved
                </span>
              </div>
            </div>

            {/* Source breakdown */}
            {open && (
              <div style={{ padding: "12px 16px" }}>
                {term.domain && (
                  <div style={{ fontSize: "0.72rem", color: T.textMuted, marginBottom: 10 }}>
                    📂 Domain: <strong style={{ color: T.textPrimary }}>{term.domain}</strong>
                  </div>
                )}
                {sources.map(srcSys => {
                  const mappings = term.mappings_by_source[srcSys];
                  return (
                    <div key={srcSys} style={{ marginBottom: 10 }}>
                      {/* Source system header */}
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "5px 10px", borderRadius: 5,
                        background: T.bgSidebar, border: `1px solid ${T.border}`,
                        marginBottom: 4,
                      }}>
                        <span style={{
                          fontSize: "0.72rem", fontWeight: 800, color: T.brand,
                          background: T.brandLight, borderRadius: 3, padding: "1px 7px",
                          border: `1px solid ${T.brandMid}`,
                        }}>🗄 {srcSys}</span>
                        <span style={{ fontSize: "0.68rem", color: T.textMuted }}>
                          {mappings.length} field{mappings.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Field rows — with approve/reject/reassign */}
                      {mappings.map((m, i) => {
                        // Apply any local optimistic override
                        const override = localOverrides[term.id]?.[m.field];
                        if (override === null) return null; // removed via reassign
                        const effectiveM = override ?? m;
                        return (
                          <div key={i} style={{ marginBottom: i < mappings.length - 1 ? 4 : 0 }}>
                            <MappingRow
                              mapping={effectiveM}
                              termId={term.id}
                              termName={term.term}
                              appNm={appNm}
                              onApproved={(fieldName, approved) =>
                                applyMappingApproved(term.id, fieldName, approved)}
                              onReassigned={(fieldName, toTermId) =>
                                applyMappingReassigned(term.id, fieldName)}
                              showNavigate={true}
                              onNavigate={() => onNavigateToField({
                                entityId:  effectiveM.entity_id,
                                srcSystem: effectiveM.src_system,
                                entity:    effectiveM.entity,
                                field:     effectiveM.field,
                              })}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ＋ Add field mapping to this term */}
            {open && (
              <CWAddButton
                appNm={appNm}
                termId={term.id}
                termName={term.term}
                defaultEntityId={allMappings[0]?.entity_id ?? ""}
                onMappingChange={onMappingChange}
              />
            )}
          </div>
        );
      })}
      <style>{`.cw-nav-hint{opacity:0} tr:hover .cw-nav-hint,div:hover>.cw-nav-hint{opacity:1!important}`}</style>
    </div>
  );
}

// Small helper so each crosswalk term card can independently show the picker
function CWAddButton({ appNm, termId, termName, defaultEntityId, onMappingChange }: {
  appNm: string; termId: string; termName: string;
  defaultEntityId: string; onMappingChange: () => void;
}) {
  const [show, setShow] = React.useState(false);
  const handleAdded = async (_f: FieldEntry) => {
    setShow(false);
    onMappingChange();
  };
  return (
    <>
      <div style={{ padding: "8px 16px", borderTop: `1px solid ${T.border}`,
        background: T.bgSidebar }}>
        <button
          onClick={() => setShow(true)}
          style={{
            padding: "5px 14px", borderRadius: 6, cursor: "pointer",
            background: T.brandLight, border: `1px dashed ${T.brandMid}`,
            color: T.brand, fontSize: "0.72rem", fontWeight: 700,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
          ＋ Add field mapping
        </button>
      </div>
      {show && (
        <FieldPickerModal
          appNm={appNm}
          mode="add"
          termId={termId}
          termName={termName}
          entityId={defaultEntityId}
          onConfirm={handleAdded}
          onClose={() => setShow(false)}
        />
      )}
    </>
  );
}

// ─── IMPORT PANEL ─────────────────────────────────────────────────────────────

function ImportPanel({ appNm, onImported }: { appNm: string; onImported: () => void }) {
  const [open,      setOpen]      = useState(false);
  const [mode,      setMode]      = useState<"excel"|"collibra"|"alation">("excel");
  const [importing, setImporting] = useState(false);
  const [msg,       setMsg]       = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) { setImporting(false); return; }
      const sep = f.name.endsWith(".tsv") ? "\t" : ",";
      const header = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/"/g, ""));
      const col = (names: string[], row: string[]) => {
        for (const n of names) {
          const idx = header.indexOf(n);
          if (idx >= 0) return (row[idx] || "").replace(/^"|"$/g, "").trim();
        }
        return "";
      };
      const terms = lines.slice(1).map(line => {
        const row = line.split(sep);
        return {
          term:        col(["term","name","business term"], row),
          description: col(["definition","description","desc","meaning"], row),
          domain:      col(["domain","data domain","category"], row),
          synonyms:    col(["synonyms","alias"], row).split(";").map(s=>s.trim()).filter(Boolean),
          source:      "excel",
          status:      "draft",
        };
      }).filter(t => t.term);

      const r = await apiPost(`/adriva_api/dg/glossary/${appNm}/upsert`, { terms });
      setImporting(false);
      if (r.success) { setMsg(`✓ Imported ${r.created} new, ${r.updated} updated`); onImported(); }
      else setMsg("Import failed");
    };
    reader.readAsText(f);
  };

  return (
    <div>
      <button onClick={() => setOpen(o => !o)} style={{
        padding: "7px 14px", borderRadius: 7, cursor: "pointer",
        background: T.brandLight, border: `1px solid ${T.brandMid}`,
        color: T.brand, fontSize: "0.78rem", fontWeight: 700,
        display: "flex", alignItems: "center", gap: 6,
      }}>
        📥 Import Terms {open ? "▴" : "▾"}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, zIndex: 200,
          width: 340, background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          padding: "16px 18px", marginTop: 4,
        }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: T.textLabel,
            textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Import Source
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {(["excel","collibra","alation"] as const).map(m => (
              <button key={m} onClick={() => setMode(m)} style={{
                flex: 1, padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                background: mode === m ? T.brandLight : T.bgCard,
                border: `1.5px solid ${mode === m ? T.brand : T.border}`,
                color: mode === m ? T.brand : T.textMuted,
                fontSize: "0.72rem", fontWeight: mode === m ? 700 : 500,
              }}>{m === "excel" ? "📄 Excel" : m === "collibra" ? "⬡ Collibra" : "◈ Alation"}</button>
            ))}
          </div>

          {mode === "excel" && (
            <div>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt"
                style={{ display: "none" }} onChange={handleFile} />
              <button onClick={() => fileRef.current?.click()} disabled={importing} style={{
                width: "100%", padding: "8px", borderRadius: 7, cursor: "pointer",
                background: T.brand, border: `1px solid ${T.brand}`,
                color: "#fff", fontSize: "0.78rem", fontWeight: 700,
                opacity: importing ? 0.7 : 1,
              }}>
                {importing ? "Importing…" : "Choose CSV / TSV file"}
              </button>
              <div style={{ fontSize: "0.68rem", color: T.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                Required columns: <code>term</code>, <code>definition</code><br />
                Optional: <code>domain</code>, <code>synonyms</code> (semicolon-separated)
              </div>
            </div>
          )}
          {(mode === "collibra" || mode === "alation") && (
            <div style={{ padding: "12px", background: T.amberBg, border: `1px solid ${T.amberBorder}`,
              borderRadius: 7, fontSize: "0.76rem", color: T.amber, lineHeight: 1.5 }}>
              Use the <strong>BusinessTermConfigModal</strong> in the DataInputPage to import from {mode === "collibra" ? "Collibra" : "Alation"} with lazy loading and term selection.
            </div>
          )}
          {msg && (
            <div style={{ marginTop: 10, fontSize: "0.76rem", fontWeight: 700,
              color: msg.startsWith("✓") ? T.green : T.red }}>
              {msg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function BusinessGlossaryPage({
  appNm, onNavigateToField,
}: BusinessGlossaryPageProps) {
  const [activeTab, setActiveTab]  = useState<"terms"|"crosswalk">("terms");

  // Term List state
  const [terms,    setTerms]   = useState<GlossaryTerm[]>([]);
  const [stats,    setStats]   = useState<GlossaryStats>({ total:0, approved:0, draft:0, generated:0, deprecated:0 });
  const [domains,  setDomains] = useState<string[]>([]);
  const [entities, setEntities]= useState<string[]>([]);
  const [total,    setTotal]   = useState(0);
  const [page,     setPage]    = useState(1);
  const [loading,  setLoading] = useState(true);

  // Filters (shared between tabs)
  const [search,   setSearch]  = useState("");
  const [domain,   setDomain]  = useState("");
  const [entity,   setEntity]  = useState("");
  const [status,   setStatus]  = useState("");

  // Expanded rows
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Bulk select
  const [selected, setSelected]= useState<Set<string>>(new Set());


  const PAGE_SIZE = 50;

  const loadTerms = useCallback(async (pg = 1) => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(pg), page_size: String(PAGE_SIZE),
      ...(search && { search }), ...(domain && { domain }),
      ...(entity && { entity }), ...(status && { status }),
    });
    const r = await apiGet(`/adriva_api/dg/glossary/${appNm}?${params}`);
    if (r.success) {
      setTerms(r.terms);
      setStats(r.stats);
      setDomains(r.domains);
      setEntities(r.entities);
      setTotal(r.total);
      setPage(pg);

    }
    setLoading(false);
  }, [appNm, search, domain, entity, status]);

  useEffect(() => { loadTerms(1); }, [loadTerms]);

  const handleApprove = async (termId: string, unapprove = false) => {
    await apiPost(`/adriva_api/dg/glossary/${appNm}/approve`, {
      term_ids: [termId], unapprove,
    });
    loadTerms(page);
  };

  const handleBulkApprove = async () => {
    if (!selected.size) return;
    await apiPost(`/adriva_api/dg/glossary/${appNm}/approve`, {
      term_ids: [...selected],
    });
    setSelected(new Set());
    loadTerms(page);
  };

  const handleDeprecate = async (termId: string) => {
    if (!window.confirm("Deprecate this term? It will be hidden but the ID is preserved.")) return;
    await apiDelete(`/adriva_api/dg/glossary/${appNm}/${termId}`);
    loadTerms(page);
  };

  const handleSave = async (termId: string, changes: Partial<GlossaryTerm>) => {
    await apiPut(`/adriva_api/dg/glossary/${appNm}/${termId}`, changes);
    loadTerms(page);
  };

  const handleExport = async (fmt: "csv"|"json") => {
    const r = await fetch(`/adriva_api/dg/glossary/${appNm}/export`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ format: fmt, filter: { domain, entity, status } }),
    });
    const blob = await r.blob();
    const cd   = r.headers.get("Content-Disposition") || "";
    const name = cd.match(/filename="([^"]+)"/)?.[1] ?? `glossary.${fmt}`;
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleExpand = (id: string) => setExpanded(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  const toggleSelect = (id: string) => setSelected(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", background: T.bg, minHeight: "100%" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Lora:wght@600;700&display=swap');
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-thumb{background:#c8d0dc;border-radius:99px;}
        input:focus,select:focus{outline:none!important;border-color:${T.brand}!important;box-shadow:0 0 0 3px ${T.brand}18!important;}
        button:hover:not(:disabled){opacity:0.88;}
        tr:hover{background:${T.bgSidebar}!important;}
      `}</style>

      {/* Header */}
      <div style={{
        padding: "16px 24px", background: T.bgCard,
        borderBottom: `1px solid ${T.border}`,
        borderTop: `3px solid ${T.brand}`,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ fontSize: "1.2rem", fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", color: T.brand, marginBottom: 4 }}>
            🏛 Enterprise Business Glossary
          </div>
          
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", position: "relative" }}>
          <ImportPanel appNm={appNm} onImported={() => loadTerms(1)} />
          <button onClick={() => handleExport("csv")} style={{
            padding: "7px 14px", borderRadius: 7, cursor: "pointer",
            background: T.bgCard, border: `1px solid ${T.border}`,
            color: T.textMuted, fontSize: "0.78rem", fontWeight: 600,
          }}>📤 CSV</button>
          <button onClick={() => handleExport("json")} style={{
            padding: "7px 14px", borderRadius: 7, cursor: "pointer",
            background: T.bgCard, border: `1px solid ${T.border}`,
            color: T.textMuted, fontSize: "0.78rem", fontWeight: 600,
          }}>📤 JSON</button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{
        display: "flex", gap: 10, padding: "12px 24px",
        background: T.bgCard, borderBottom: `1px solid ${T.border}`,
        flexWrap: "wrap",
      }}>
        {[
          { label: "Total",      val: stats.total,     color: T.brand },
          { label: "✓ Approved", val: stats.approved,  color: T.green },
          { label: "⏳ Draft",   val: stats.draft,     color: T.amber },
          { label: "🤖 Generated",val: stats.generated, color: T.purple },
          { label: "✕ Deprecated",val: stats.deprecated,color: T.textMuted },
        ].map(({ label, val, color }) => (
          <div key={label} style={{
            padding: "8px 16px", borderRadius: 8, background: T.bgSidebar,
            border: `1px solid ${T.border}`, textAlign: "center", minWidth: 100,
          }}>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: "0.65rem", color: T.textMuted, marginTop: 3,
              textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div style={{
        display: "flex", background: T.bgCard, borderBottom: `1px solid ${T.border}`,
        padding: "0 24px",
      }}>
        {([
          { key: "terms",     label: "📚 Term List" },
          { key: "crosswalk", label: "🔗 Crosswalk View" },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: "10px 20px", border: "none", cursor: "pointer",
            background: activeTab === t.key ? T.bgCard : "transparent",
            borderBottom: activeTab === t.key ? `2px solid ${T.brand}` : "2px solid transparent",
            color: activeTab === t.key ? T.textPrimary : T.textMuted,
            fontSize: "0.82rem", fontWeight: activeTab === t.key ? 700 : 500,
            marginBottom: activeTab === t.key ? -1 : 0,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        display: "flex", gap: 10, padding: "12px 24px",
        background: T.bgSidebar, borderBottom: `1px solid ${T.border}`,
        flexWrap: "wrap", alignItems: "center",
      }}>
        <input
          placeholder="🔍 Search terms…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputSt, width: 220 }}
        />
        <select value={domain} onChange={e => setDomain(e.target.value)}
          style={{ ...inputSt, width: 160 }}>
          <option value="">All Domains</option>
          {domains.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={entity} onChange={e => setEntity(e.target.value)}
          style={{ ...inputSt, width: 160 }}>
          <option value="">All Entities</option>
          {entities.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ ...inputSt, width: 150 }}>
          <option value="">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="draft">Draft</option>
          <option value="generated">Generated</option>
          <option value="deprecated">Deprecated</option>
        </select>
        {(search || domain || entity || status) && (
          <button onClick={() => { setSearch(""); setDomain(""); setEntity(""); setStatus(""); }}
            style={{ padding: "7px 12px", borderRadius: 6, cursor: "pointer",
              background: T.bgCard, border: `1px solid ${T.border}`,
              color: T.textMuted, fontSize: "0.74rem" }}>
            ✕ Clear
          </button>
        )}
        {activeTab === "terms" && selected.size > 0 && (
          <button onClick={handleBulkApprove} style={{
            marginLeft: "auto", padding: "7px 16px", borderRadius: 7, cursor: "pointer",
            background: T.green, border: `1px solid ${T.green}`,
            color: "#fff", fontSize: "0.78rem", fontWeight: 700,
          }}>
            ✓ Approve {selected.size} selected
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "16px 24px", overflowY: "auto" }}>

        {/* ── Term List Tab ── */}
        {activeTab === "terms" && (
          <>
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                padding: 60, flexDirection: "column", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%",
                  border: `3px solid ${T.brandMid}`, borderTopColor: T.brand,
                  animation: "spin 0.8s linear infinite" }} />
                <span style={{ fontSize: "0.84rem", color: T.textMuted }}>Loading terms…</span>
              </div>
            ) : terms.length === 0 ? (
              <div style={{ padding: "48px 24px", textAlign: "center", color: T.textMuted }}>
                <div style={{ fontSize: "2rem", marginBottom: 10 }}>📚</div>
                <div style={{ fontWeight: 700, color: T.textSecond, marginBottom: 6 }}>
                  No business terms yet
                </div>
                <div style={{ fontSize: "0.82rem" }}>
                  Run a DG discovery to generate terms automatically, or import from Excel/Collibra/Alation.
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: "0.76rem", color: T.textMuted }}>
                    Showing {terms.length} of {total} terms
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: 6,
                    fontSize: "0.74rem", color: T.textMuted, cursor: "pointer" }}>
                    <input type="checkbox"
                      checked={selected.size === terms.length && terms.length > 0}
                      onChange={e => setSelected(e.target.checked ? new Set(terms.map(t => t.id)) : new Set())}
                    />
                    Select all on page
                  </label>
                </div>

                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`,
                  borderRadius: 10, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: T.bgSidebar, borderBottom: `1px solid ${T.border}` }}>
                        <th style={{ width: 32, padding: "8px 12px" }}></th>
                        {["Term","Description","Domain","Status","Source","Sources","Actions"].map(h => (
                          <th key={h} style={{
                            padding: "8px 12px", textAlign: "left",
                            fontSize: "0.62rem", fontWeight: 700, color: T.textMuted,
                            textTransform: "uppercase", letterSpacing: "0.08em",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {terms.map(t => (
                        <TermRow
                          key={t.id}
                          term={t}
                          expanded={expanded.has(t.id)}
                          onToggle={() => toggleExpand(t.id)}
                          onApprove={handleApprove}
                          onDeprecate={handleDeprecate}
                          onSave={handleSave}
                          appNm={appNm}
                          onMappingChange={() => loadTerms(page)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
                    <button onClick={() => loadTerms(page - 1)} disabled={page <= 1} style={{
                      padding: "5px 12px", borderRadius: 6, cursor: page > 1 ? "pointer" : "default",
                      background: T.bgCard, border: `1px solid ${T.border}`,
                      color: page > 1 ? T.textPrimary : T.textMuted,
                      fontSize: "0.76rem",
                    }}>← Prev</button>
                    <span style={{ padding: "5px 12px", fontSize: "0.76rem", color: T.textMuted }}>
                      Page {page} of {totalPages}
                    </span>
                    <button onClick={() => loadTerms(page + 1)} disabled={page >= totalPages} style={{
                      padding: "5px 12px", borderRadius: 6, cursor: page < totalPages ? "pointer" : "default",
                      background: T.bgCard, border: `1px solid ${T.border}`,
                      color: page < totalPages ? T.textPrimary : T.textMuted,
                      fontSize: "0.76rem",
                    }}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Crosswalk Tab ── */}
        {activeTab === "crosswalk" && (
          <CrosswalkTab
            appNm={appNm}
            filters={{ domain, entity, status, search }}
            onNavigateToField={onNavigateToField}
            onMappingChange={() => loadTerms(page)}
          />
        )}
      </div>
    </div>
  );
}